import React, { useEffect, useState } from 'react';
import ConfirmacaoModal from './ConfirmacaoModal';
import { fetchPreparoPedidos, FornecedorDoPedido } from '../../../api/portalCotacoes/portalCotacoesApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Confirmação do "Gerar pedidos" com a ESCOLHA da forma de pagamento de cada pedido
 * (portal-cotacoes/at-15, decisão do Victor de 28/10/2026).
 *
 * 🔴 Desde a portal-cotacoes/at-16 o fornecedor NÃO marca mais forma de pagamento (reunião de
 * Compras de 28/10/2026): o comprador escolhe aqui, entre todas as formas configuradas, a que foi
 * combinada com ele. A marcação de propostas antigas aparece só como informação.
 *
 * 🔴 Sem a lista carregada, o botão fica bloqueado: gerar sem ela mandaria um pedido sem cobrança
 * ou com uma que ninguém escolheu (regra da at-60).
 *
 * Forma sem tradução para o Nasajon aparece, mas desabilitada e dita.
 */

interface GerarPedidosModalProps {
  isOpen: boolean;
  cotacaoId: string;
  numero: string;
  carregando: boolean;
  onConfirmar: (formasPagamento: Record<string, string>) => void;
  onCancelar: () => void;
}

const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const GerarPedidosModal: React.FC<GerarPedidosModalProps> = ({
  isOpen,
  cotacaoId,
  numero,
  carregando,
  onConfirmar,
  onCancelar,
}) => {
  const [fornecedores, setFornecedores] = useState<FornecedorDoPedido[] | null>(null);
  const [erro, setErro] = useState(false);
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    let vivo = true;
    setFornecedores(null);
    setErro(false);
    setEscolhas({});
    fetchPreparoPedidos(cotacaoId)
      .then((r) => {
        if (!vivo) return;
        setFornecedores(r.fornecedores);
        // Uma só forma configurada já vem escolhida: é a única resposta possível.
        const iniciais: Record<string, string> = {};
        for (const f of r.fornecedores) {
          const configuradas = f.formas.filter((x) => x.configurada);
          if (!f.jaEnviado && configuradas.length === 1) iniciais[f.cnpj] = configuradas[0].opcao;
        }
        setEscolhas(iniciais);
      })
      .catch((error) => {
        if (!vivo) return;
        setErro(true);
        notify.errorFrom(
          error,
          'Não foi possível carregar as formas de pagamento dos fornecedores. Recarregue a página; se persistir, acione a TI.',
        );
      });
    return () => {
      vivo = false;
    };
  }, [isOpen, cotacaoId]);

  const pendentes = (fornecedores ?? []).filter((f) => !f.jaEnviado);
  const completo = fornecedores !== null && pendentes.every((f) => Boolean(escolhas[f.cnpj]));

  const descricao = (
    <>
      <p style={{ marginTop: 0 }}>
        Será criado <strong>um pedido por fornecedor vencedor</strong>, todos com a referência{' '}
        <strong>{numero}</strong>. Escolha a forma de pagamento combinada com cada fornecedor (o que ele
        escreveu sobre pagamento está na proposta dele). Enquanto o módulo estiver em modo de simulação, os pedidos são registrados aqui mas não
        chegam ao ERP.
      </p>

      {erro && (
        <p className="erro-de-tela">
          Não foi possível carregar as formas de pagamento dos fornecedores, então os pedidos não podem
          ser gerados agora. Feche e abra de novo; se persistir, acione a TI.
        </p>
      )}
      {!erro && fornecedores === null && <p className="pc-hint">Carregando os fornecedores...</p>}

      {fornecedores?.map((f) => (
        <div key={f.cnpj} className="pc-campo">
          <label className="pc-label" htmlFor={`forma-${f.cnpj}`} title={f.nome}>
            {f.nome} · {moeda(f.total)}
          </label>
          {f.jaEnviado ? (
            <p className="pc-hint">Pedido já enviado: nada a escolher para este fornecedor.</p>
          ) : f.formas.length === 0 ? (
            <p className="erro-de-tela">
              Nenhuma forma de pagamento está cadastrada, então o pedido não pode ser gerado. Cadastre em
              Administração do Sistema → Cotações → Formas de pagamento.
            </p>
          ) : (
            <>
              <select
                id={`forma-${f.cnpj}`}
                className="pc-select"
                value={escolhas[f.cnpj] ?? ''}
                onChange={(e) => setEscolhas((atual) => ({ ...atual, [f.cnpj]: e.target.value }))}
              >
                <option value="">Escolha a forma de pagamento</option>
                {f.formas.map((x) => (
                  <option key={x.opcao} value={x.opcao} disabled={!x.configurada}>
                    {x.configurada ? x.opcao : `${x.opcao} (não configurada no Nasajon)`}
                    {x.marcadaPeloFornecedor ? ' · marcada pelo fornecedor' : ''}
                  </option>
                ))}
              </select>
              {f.formas.every((x) => !x.configurada) && (
                <p className="pc-hint">
                  Nenhuma forma de pagamento foi ligada ao Nasajon ainda. Configure em
                  Administração do Sistema → Cotações → Formas de pagamento.
                </p>
              )}
            </>
          )}
        </div>
      ))}
    </>
  );

  return (
    <ConfirmacaoModal
      isOpen={isOpen}
      titulo="Gerar os pedidos no ERP?"
      descricao={descricao}
      rotuloConfirmar="Gerar pedidos"
      carregando={carregando}
      desabilitado={!completo}
      onConfirmar={() => onConfirmar(escolhas)}
      onCancelar={onCancelar}
    />
  );
};

export default GerarPedidosModal;
